import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { resetPassword } from '../../api/auth.api';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
      if (!token) {
          setError('Token no válido o no proporcionado.');
      }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    if (!token) {
        setError('Token inválido.');
        setIsLoading(false);
        return;
    }

    if (password.length < 6) {
        setError('La contraseña debe tener al menos 6 caracteres.');
        setIsLoading(false);
        return;
    }

    if (password !== confirmPassword) {
        setError('Las contraseñas no coinciden.');
        setIsLoading(false);
        return;
    }

    try {
      const response = await resetPassword(token, password);
      setSuccess(response.message);
      // Opcional: Redirigir después de un tiempo
      setTimeout(() => {
          navigate('/login');
      }, 3000);
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
         const axiosError = err as { response?: { data?: { message?: string } } };
         setError(axiosError.response?.data?.message || 'Error al restablecer la contraseña');
      } else {
        setError('Error de conexión. Inténtalo más tarde.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
             <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                    <i className="ri-error-warning-line text-3xl text-red-600"></i>
                </div>
                <h2 className="text-xl font-bold text-slate-800 mb-2">Enlace Inválido</h2>
                <p className="text-slate-600 mb-6">El enlace de recuperación no es válido o está incompleto.</p>
                <Link to="/forgot-password" className="text-teal-600 hover:text-teal-700 font-medium">
                    Solicitar un nuevo enlace
                </Link>
             </div>
        </div>
      );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-500 rounded-2xl mb-4">
              <i className="ri-lock-password-line text-3xl text-white"></i>
            </div>
            <h1 className="text-2xl font-bold text-slate-800 mb-2">
              Nueva Contraseña
            </h1>
            <p className="text-slate-500 text-sm">
              Ingresa tu nueva contraseña para asegurar tu cuenta.
            </p>
          </div>

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
              <div className="flex items-center gap-2">
                <i className="ri-check-line text-green-600"></i>
                <p className="text-sm text-green-600">{success}</p>
              </div>
              <p className="text-xs text-green-700 mt-2 pl-6">Redirigiendo al login...</p>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-center gap-2">
                <i className="ri-error-warning-line text-red-600"></i>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          )}

          {!success && (
            <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                    Nueva Contraseña
                </label>
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i className="ri-lock-line text-slate-400"></i>
                    </div>
                    <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition text-sm"
                    placeholder="••••••••"
                    required
                    disabled={isLoading}
                    minLength={6}
                    />
                    <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                    >
                    <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-slate-400 hover:text-slate-600`}></i>
                    </button>
                </div>
                </div>

                <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                    Confirmar Contraseña
                </label>
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i className="ri-lock-check-line text-slate-400"></i>
                    </div>
                    <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition text-sm"
                    placeholder="••••••••"
                    required
                    disabled={isLoading}
                    />
                </div>
                </div>

                <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white font-medium py-3 rounded-xl transition duration-200 shadow-lg shadow-teal-500/30 whitespace-nowrap flex items-center justify-center gap-2"
                >
                {isLoading ? (
                    <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Actualizando...
                    </>
                ) : (
                    'Restablecer Contraseña'
                )}
                </button>
            </form>
          )}

           {!success && (
                <div className="text-center pt-2">
                    <Link to="/login" className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center justify-center gap-1">
                        <i className="ri-arrow-left-line"></i>
                        Cancelar
                    </Link>
                </div>
           )}
        </div>
      </div>
    </div>
  );
}
